import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { 
  faTag, faUnlink, faTimes, faSpinner, faSearch, faCheck
} from '@fortawesome/free-solid-svg-icons';
import { TagsService, Tag } from '../../services/tags.service';
import { AlertService } from '../alert-service/alert-service';
import { ModalTagDetailsComponent } from '../modal-tag-details/modal-tag-details.component';
import { forkJoin, Observable } from 'rxjs';
import { normalizeForSearch } from '../../utils/text-search.util';

export interface TaggableTag {
  id: string;
  name: string;
  text_color: string;
  bg_color: string;
}

export interface TaggableItem {
  id: string;
  name: string;
  tag?: TaggableTag | null;
  tags?: TaggableTag[];
}

@Component({
  selector: 'app-tag-manager',
  standalone: true,
  imports: [FormsModule, FontAwesomeModule, TranslateModule, ModalTagDetailsComponent],
  templateUrl: './tag-manager.component.html',
  styleUrls: ['./tag-manager.component.scss']
})
export class TagManagerComponent implements OnInit {
  @Input() entityType: string = '';
  @Input() entityName: string = '';
  @Input() maxTags = 1;
  @Output() tagChanged = new EventEmitter<void>();

  showTagLinkModal = false;
  showTagUnlinkModal = false;
  showTagDescriptionModal = false;
  selectedItem: TaggableItem | null = null;
  availableTags: Tag[] = [];
  filteredTags: Tag[] = [];
  selectedTag: Tag | null = null;
  selectedTags: Tag[] = [];
  tagSearchTerm = '';
  linkingTag = false;
  unlinkingTag = false;
  loadingTags = false;

  faTag = faTag;
  faUnlink = faUnlink;
  faTimes = faTimes;
  faSpinner = faSpinner;
  faSearch = faSearch;
  faCheck = faCheck;

  constructor(
    private tagsService: TagsService,
    private translate: TranslateService,
    private alertService: AlertService
  ) {}

  ngOnInit() {
    this.loadAvailableTags();
  }

  private loadAvailableTags(): void {
    this.loadingTags = true;
    this.tagsService.getTags().subscribe({
      next: (response) => {
        this.availableTags = response.tags || [];
        this.filteredTags = [...this.availableTags];
        this.loadingTags = false;
      },
      error: (err: any) => {
        this.alertService.showError(err);
        this.loadingTags = false;
      }
    });
  }

  openTagLinkModal(item: TaggableItem) {
    if (!item?.id) return;

    this.selectedItem = item;
    this.selectedTag = null;
    this.tagSearchTerm = '';
    this.showTagLinkModal = true;
    this.filteredTags = [...this.availableTags];

    if (this.maxTags > 1) {
      this.selectedTags = (item.tags || []).map(t => ({
        id: t.id,
        name: t.name,
        text_color: t.text_color,
        bg_color: t.bg_color
      }));
    }
  }

  closeTagLinkModal() {
    this.showTagLinkModal = false;
    this.selectedItem = null;
    this.selectedTag = null;
    this.selectedTags = [];
    this.tagSearchTerm = '';
    this.linkingTag = false;
  }

  openTagUnlinkModal(item: TaggableItem) {
    if (!item?.id) return;
    
    this.selectedItem = item;
    this.showTagUnlinkModal = true;
  }

  closeTagUnlinkModal() {
    this.showTagUnlinkModal = false;
    this.selectedItem = null;
    this.unlinkingTag = false;
  }

  filterTags() {
    if (!this.tagSearchTerm.trim()) {
      this.filteredTags = [...this.availableTags];
      return;
    }
    
    const term = normalizeForSearch(this.tagSearchTerm);
    this.filteredTags = this.availableTags.filter(tag =>
      normalizeForSearch(tag.name).includes(term)
    );
  }

  selectTag(tag: Tag) {
    if (this.maxTags > 1) {
      const alreadySelected = this.selectedTags.some(t => t.id === tag.id);
      if (alreadySelected) {
        this.selectedTags = this.selectedTags.filter(t => t.id !== tag.id);
      } else if (this.selectedTags.length < this.maxTags) {
        this.selectedTags = [...this.selectedTags, tag];
      } else {
        this.alertService.showError('MODAL.MANAGE.TAG.MAX_REACHED');
      }
      return;
    }
    this.selectedTag = this.selectedTag?.id === tag.id ? null : tag;
  }

  isSelected(tag: Tag): boolean {
    if (this.maxTags > 1) {
      return this.selectedTags.some(t => t.id === tag.id);
    }
    return this.selectedTag?.id === tag.id;
  }

  confirmLinkTag() {
    if (this.maxTags > 1) {
      this.confirmMultiTags();
      return;
    }

    if (!this.selectedTag || !this.selectedItem?.id || !this.entityType) return;

    this.linkingTag = true;

    this.tagsService.linkTag(this.selectedTag.id, this.entityType, this.selectedItem.id).subscribe({
      next: (response) => {
        this.alertService.showSuccess(response.message);
        this.tagChanged.emit();
        this.closeTagLinkModal();
      },
      error: (err: any) => {
        this.alertService.showError(err);
        this.linkingTag = false;
      }
    });
  }

  private confirmMultiTags() {
    if (!this.selectedItem?.id || !this.entityType) return;

    const itemId = this.selectedItem.id;
    const original = this.selectedItem.tags || [];
    const originalIds = new Set(original.map(t => t.id));
    const selectedIds = new Set(this.selectedTags.map(t => t.id));

    const toAdd = this.selectedTags.filter(t => !originalIds.has(t.id));
    const toRemove = original.filter(t => !selectedIds.has(t.id));

    if (toAdd.length === 0 && toRemove.length === 0) {
      this.closeTagLinkModal();
      return;
    }

    this.linkingTag = true;
    const operations: Observable<any>[] = [
      ...toRemove.map(t => this.tagsService.unlinkTag(t.id, this.entityType, itemId)),
      ...toAdd.map(t => this.tagsService.linkTag(t.id, this.entityType, itemId))
    ];

    forkJoin(operations).subscribe({
      next: () => {
        this.alertService.showSuccess('MODAL.MANAGE.TAG.SUCCESS');
        this.tagChanged.emit();
        this.closeTagLinkModal();
      },
      error: (err: any) => {
        this.alertService.showError(err);
        this.linkingTag = false;
        this.tagChanged.emit();
      }
    });
  }

  confirmUnlinkTag() {
    if (!this.selectedItem?.id || !this.selectedItem?.tag?.id || !this.entityType) return;

    this.unlinkingTag = true;
    
    this.tagsService.unlinkTag(this.selectedItem.tag.id, this.entityType, this.selectedItem.id).subscribe({
      next: (response) => {
        this.alertService.showSuccess(response.message);
        this.tagChanged.emit();
        this.closeTagUnlinkModal();
      },
      error: (err: any) => {
        this.alertService.showError(err);
        this.unlinkingTag = false;
      }
    });
  }

  public openLinkModal(item: TaggableItem) {
    this.openTagLinkModal(item);
  }

  public openUnlinkModal(item: TaggableItem) {
    this.openTagUnlinkModal(item);
  }

  public openTagDescription(tag: any) {
    if (!tag) return;
    
    this.loadingTags = true;
    this.tagsService.getTagById(tag.id).subscribe({
      next: (fullTag) => {
        this.selectedTag = fullTag;
        this.showTagDescriptionModal = true;
        this.loadingTags = false;
      },
      error: (err: any) => {
        this.alertService.showError(err);
        this.loadingTags = false;
      }
    });
  }

  closeTagDescriptionModal() {
    this.showTagDescriptionModal = false;
    this.selectedTag = null;
  }
}
